<script lang="ts">
  import type { Api, OrgDetail, User } from "../lib/api";
  import { ApiError } from "../lib/api";
  import Card from "../lib/ui/Card.svelte";
  import Button from "../lib/ui/Button.svelte";
  import Pill from "../lib/ui/Pill.svelte";
  import Banner from "../lib/ui/Banner.svelte";
  import Spinner from "../lib/ui/Spinner.svelte";
  import EmptyState from "../lib/ui/EmptyState.svelte";
  import FieldCoverage from "../lib/ui/FieldCoverage.svelte";
  import GroupsCard from "./org/GroupsCard.svelte";
  import PlanCard from "./org/PlanCard.svelte";
  import Searches from "./Searches.svelte";
  import { seg, navigate } from "../lib/route.svelte";
  import {
    Button as UiButton,
    Input,
    Select,
    SelectTrigger,
    SelectContent,
    SelectItem,
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
    dialogs,
  } from "@price-check/ui";

  const ROLES = ["member", "admin", "owner"];

  let { api, orgId, onClose }: { api: Api; orgId: string; onClose: () => void } = $props();

  let detail = $state<OrgDetail | null>(null);
  let error = $state("");
  let memberError = $state("");
  let memberEmail = $state("");
  let memberPassword = $state("");
  let memberRole = $state("member");
  let appUsers = $state<User[]>([]);
  const ORG_TABS = ["users", "vendors", "searches"];
  const activeTab = $derived(ORG_TABS.includes(seg(2) ?? "") ? (seg(2) as string) : "users");

  async function load() {
    error = "";
    try {
      detail = await api.orgDetail(orgId);
    } catch (e) {
      error = e instanceof Error ? e.message : "failed to load";
    }
  }
  $effect(() => {
    void load();
  });
  // App users power the add-member suggestions (portal admins can't be members).
  $effect(() => {
    api
      .listUsers()
      .then((u) => (appUsers = u.filter((x) => x.role !== "portal")))
      .catch(() => {});
  });

  const memberIds = $derived(new Set(detail?.members.map((m) => m.user_id) ?? []));
  const suggestions = $derived(
    appUsers.filter((u) => !memberIds.has(u.id)).map((u) => u.email),
  );

  async function rename() {
    if (!detail) return;
    const name = (
      await dialogs.prompt({
        title: "Rename organization",
        defaultValue: detail.org.name,
        confirmLabel: "Rename",
      })
    )?.trim();
    if (!name) return;
    await api.renameOrg(orgId, name);
    await load();
  }
  async function remove() {
    if (!detail) return;
    if (
      !(await dialogs.confirm({
        title: "Delete organization",
        message: `Delete "${detail.org.name}"? This removes its members + config settings.`,
        confirmLabel: "Delete",
        tone: "danger",
      }))
    )
      return;
    await api.deleteOrg(orgId);
    onClose();
  }
  async function addMember(e: Event) {
    e.preventDefault();
    memberError = "";
    const email = memberEmail.trim().toLowerCase();
    if (!email) return;
    const pw = memberPassword.trim();
    try {
      await api.addMember(orgId, email, memberRole);
    } catch (err) {
      // Unknown email → onboard a NEW app user (set a password, or invite if blank)
      // then add them to this org. App users are managed here, under the org.
      if (err instanceof ApiError && err.status === 404) {
        try {
          await api.createUser(email, "app", pw || undefined);
          await api.addMember(orgId, email, memberRole);
        } catch (err2) {
          memberError = err2 instanceof ApiError ? err2.message : "could not add member";
          return;
        }
      } else {
        memberError = err instanceof ApiError ? err.message : "could not add member";
        return;
      }
    }
    memberEmail = "";
    memberPassword = "";
    await load();
  }
  async function resendInvite(email: string) {
    memberError = "";
    try {
      await api.inviteUser(email);
    } catch (err) {
      memberError = err instanceof ApiError ? err.message : "could not resend invite";
    }
  }
  async function resetMemberPassword(email: string) {
    const pw = (
      await dialogs.prompt({
        title: "Reset password",
        message: `New password for ${email} (min 8 chars):`,
        inputType: "password",
        confirmLabel: "Reset",
      })
    )?.trim();
    if (!pw) return;
    memberError = "";
    try {
      await api.resetUserPassword(email, pw);
    } catch (err) {
      memberError = err instanceof ApiError ? err.message : "could not reset password";
    }
  }
  async function changeRole(userId: string, role: string) {
    memberError = "";
    try {
      await api.setMemberRole(orgId, userId, role);
      await load();
    } catch (err) {
      memberError = err instanceof ApiError ? err.message : "could not change role";
      await load(); // re-sync the select back to the real value
    }
  }
</script>

<UiButton variant="link" size="sm" class="mb-4 px-0" onclick={onClose}>
  ← All organizations
</UiButton>

{#if error}<Banner tone="danger">{error}</Banner>{/if}

{#if !detail}
  <Spinner label="Loading organization…" />
{:else}
  <div class="flex items-center gap-3 mb-5">
    <h2 class="m-0 text-xl font-extrabold">{detail.org.name}</h2>
    <div class="ml-auto flex gap-2">
      <Button variant="secondary" size="sm" onclick={rename}>Rename</Button>
      <Button variant="danger" size="sm" onclick={remove}>Delete</Button>
    </div>
  </div>

  <div class="mb-5">
    <PlanCard
      {api}
      {orgId}
      plan={detail.org.plan}
      trialEndsAt={detail.org.trial_ends_at}
      onSaved={() => void load()}
    />
  </div>

  <Tabs value={activeTab} onValueChange={(v) => navigate(`/orgs/${orgId}/${v}`, { replace: true })}>
    <TabsList>
      <TabsTrigger value="users">Users</TabsTrigger>
      <TabsTrigger value="vendors">Vendors</TabsTrigger>
      <TabsTrigger value="searches">Searches</TabsTrigger>
    </TabsList>

    <TabsContent value="users">
      <Card
        title="Members"
        subtitle="App users who can run searches for this org. A new email is invited (or set a password)."
      >
        <form class="flex flex-wrap gap-2 mb-3 items-center" onsubmit={addMember}>
          <Input
            class="flex-1 min-w-0"
            placeholder="user email"
            list="org-member-suggestions"
            bind:value={memberEmail}
          />
          <datalist id="org-member-suggestions">
            {#each suggestions as s (s)}<option value={s}></option>{/each}
          </datalist>
          <Input
            class="flex-1 min-w-0"
            type="password"
            placeholder="password (invite if blank)"
            autocomplete="new-password"
            bind:value={memberPassword}
          />
          <Select type="single" value={memberRole} onValueChange={(v) => v && (memberRole = v)}>
            <SelectTrigger size="sm" aria-label="Member role">{memberRole}</SelectTrigger>
            <SelectContent>
              {#each ROLES as r (r)}<SelectItem value={r} label={r} />{/each}
            </SelectContent>
          </Select>
          <Button type="submit" size="sm">Add</Button>
        </form>
        {#if memberError}<div class="mb-3"><Banner tone="danger">{memberError}</Banner></div>{/if}

        {#if detail.members.length === 0}
          <p class="text-muted text-sm">No members yet.</p>
        {:else}
          <ul class="list-none m-0 p-0">
            {#each detail.members as m (m.user_id)}
              <li
                class="flex items-center justify-between gap-2.5 py-2.5 text-sm
                  border-b border-border last:border-b-0"
              >
                <span class="flex items-center gap-2 min-w-0">
                  <span class="font-semibold min-w-0 overflow-hidden text-ellipsis">{m.email}</span>
                  {#if m.pending}<Pill tone="amber">Invite pending</Pill>{/if}
                </span>
                <span class="flex items-center gap-2.5 shrink-0">
                  <Select
                    type="single"
                    value={m.role}
                    onValueChange={(v) => v && changeRole(m.user_id, v)}
                  >
                    <SelectTrigger size="sm" aria-label="Role for {m.email}">{m.role}</SelectTrigger>
                    <SelectContent>
                      {#each ROLES as r (r)}<SelectItem value={r} label={r} />{/each}
                    </SelectContent>
                  </Select>
                  {#if m.pending}
                    <UiButton variant="ghost" size="sm" onclick={() => resendInvite(m.email)}>
                      Resend invite
                    </UiButton>
                  {/if}
                  <UiButton variant="ghost" size="sm" onclick={() => resetMemberPassword(m.email)}>
                    Reset password
                  </UiButton>
                  <UiButton
                    variant="destructive-ghost"
                    size="sm"
                    onclick={() => api.removeMember(orgId, m.user_id).then(load)}
                  >
                    Remove
                  </UiButton>
                </span>
              </li>
            {/each}
          </ul>
        {/if}
      </Card>
    </TabsContent>

    <TabsContent value="vendors">
      <div class="flex flex-col gap-5">
        <GroupsCard {api} {orgId} suppliers={detail.configs} />

        <Card title="Suppliers" subtitle="Suppliers this org receives.">
          {#if detail.configs.length === 0}
            <EmptyState icon="◎" title="No suppliers" hint="No distributor configs available yet." />
          {:else}
            <ul class="list-none m-0 p-0">
              {#each detail.configs as c (c.id)}
                <li
                  class="flex items-start justify-between gap-2.5 py-2.5 text-sm
                    border-b border-border last:border-b-0"
                >
                  <div class="min-w-0">
                    <span class="font-semibold">{c.name}</span>
                    <div class="mt-1.5">
                      <FieldCoverage fields={c.mappedFields} />
                    </div>
                  </div>
                  {#if c.enabled}<Pill tone="ok">on</Pill>{:else}<Pill tone="slate">off</Pill>{/if}
                </li>
              {/each}
            </ul>
          {/if}
        </Card>
      </div>
    </TabsContent>

    <TabsContent value="searches">
      <Searches {api} orgId={detail.org.id} orgName={detail.org.name} />
    </TabsContent>
  </Tabs>
{/if}
